import { ChevronRight, Network, Rows3 } from "lucide-react";
import * as React from "react";

import {
  designatedFanout,
  type PhaseLaneChild,
  type PhaseLane as PhaseLaneModel,
  type PhaseLaneBand,
  type PhaseLaneStep,
} from "@/components/dag/phase-lane";
import { formatDuration } from "@/components/runs/run-duration";
import { STATE_LABEL, STATE_STRIPE } from "@/components/runs/run-state-label";
import { truncateMiddle } from "@/lib/hash-display";
import { cn } from "@/lib/utils";

/**
 * **The phase lane's rendering (VIS/V2).** `phase-lane.ts` decides what the
 * bands ARE and carries the argument for why; this file draws them.
 *
 * It is body content of a `VisualPanel` and paints nothing of its own beyond
 * the neutral ladder the container already sets — `--panel-well` for a band's
 * ground, `--border-subtle`, `--muted-foreground`. A band is a WELL rather than
 * a card on purpose: the well is the step the panel already cuts for a grouped
 * control, so the lane sits INSIDE the container's form instead of growing a
 * second panel vocabulary beside it (`components/viz/VisualPanel.tsx`).
 *
 * **Every place hue appears is DATA**, which is the container's rule about
 * where accent may be spent: the lifecycle fill on a step that has a run behind
 * it, the same fill on each of that step's fan-out children, and the fan-out
 * width. None is hue ALONE — every fill is followed by the state said out loud
 * for a screen reader, and the width carries its `×` and its digits.
 *
 * **The bands are a SPAN of single steps, and exactly one fan-out is
 * enumerated beside them** (flowstack#1377). Each step is one row carrying its
 * state, its name and its width — a count, the way the canvas's tiles say
 * `11.8s · 6 fragments` — and the per-child rows #1281 added live in their own
 * lane, offset out of the bands and headed with the step they belong to. #1281
 * drew that enumeration under EVERY step, which on a run with four three-way
 * fan-outs is twelve rows of run id before the fold: the span the canvas draws
 * disappears under its own detail. `designatedFanout` picks the one lane —
 * the selected step, else the frontier — and the argument lives there.
 *
 * **The child rows themselves are unchanged** (flowstack#1281): one row per
 * child run, with its own state, its own id and its own duration. `×6` on a
 * six-way fan-out where one child failed names none of them, and that is the
 * case AFFDO parity debugging keeps hitting. They are capped — see
 * {@link CHILD_ROW_LIMIT} — because an unbounded list is the thumbnail problem
 * this surface exists to avoid.
 *
 * **Every step is named.** The lane summarises the EDGES, never the nodes: all
 * fifteen steps of a full AFFDO flow appear here, in reading order, and what
 * has gone is the thirty-seven arrows between them. A summary that dropped
 * steps would be a picture of a different flow with no way for the reader to
 * tell.
 *
 * **It WRAPS rather than scrolling.** The read is left to right and the lane
 * lays out that way, but five bands forced onto one line in a 400 px pane is
 * five columns of ellipsis — the thumbnail's problem again — and a horizontal
 * scrollbar on a surface whose whole claim is "readable without zooming" is
 * worse. So bands wrap like text: still first to last, and every identifier
 * legible.
 *
 * It is an `<ol>` because the order carries the meaning; the chevrons between
 * bands are decoration and are `aria-hidden`.
 */
export interface PhaseLaneProps {
  lane: PhaseLaneModel;
  /** The lane's accessible name — the surface knows which flow this is. */
  label: string;
  /**
   * Report a step click to the caller.
   *
   * Supplied, each step becomes a button and the caller renders the detail —
   * which is how the run workspace keeps the graph's "click a step, read its
   * settings in the rail" affordance while the lane is the pane's view.
   * Omitted, the steps are plain text: a lane with no detail to open must not
   * grow buttons that do nothing, the same rule `DAGCanvasProps.onNodeSelect`
   * follows one surface up.
   */
  onSelectStep?: (name: string) => void;
  /**
   * The caller's current selection, so the lane can mark it — and so the
   * fan-out lane follows it: selecting a fanned step points the enumeration at
   * that step (see `designatedFanout`).
   *
   * Two controls move it, and they move the same thing: the step's name, and
   * the step's `×N` width mark (flowstack#1398). The second is the one a reader
   * who wants to see the children actually reaches for, and until it was wired
   * it was a `<span>` that swallowed the press.
   */
  selectedStep?: string | null;
  className?: string;
}

export function PhaseLane({
  lane,
  label,
  onSelectStep,
  selectedStep = null,
  className,
}: PhaseLaneProps): JSX.Element {
  // ONE fan-out enumerated, never one per step — `designatedFanout` carries
  // the rule and the canvas it comes from (flowstack#1377).
  const fanout = designatedFanout(lane, selectedStep);
  return (
    // The span and the fan-out lane are SIBLINGS, which is the whole fix: the
    // bands stay a row of single steps and the enumeration sits beside them,
    // the way the canvas offsets its fan-out lane out of the main row. It
    // drops under the span below `pair` (960px) — the width at which the run
    // workspace already stops pairing anything.
    <div className={cn("flex flex-col gap-3 pair:flex-row pair:items-start", className)}>
      <ol aria-label={label} className="flex min-w-0 flex-1 flex-wrap items-stretch gap-2">
        {lane.bands.map((band, index) => (
          <li
            // Bands are positional by construction — contiguous runs of one
            // derived order — and two of them legitimately carry the same chain
            // (see the table in `phase-lane.ts`), so the index IS the identity
            // here rather than standing in for one.
            key={index}
            className="flex min-w-[12rem] flex-1 items-stretch gap-2"
          >
            <Band
              band={band}
              onSelectStep={onSelectStep}
              selectedStep={selectedStep}
              designatedStep={fanout?.name ?? null}
            />
            {index < lane.bands.length - 1 && (
              <ChevronRight
                aria-hidden
                className="my-auto h-4 w-4 shrink-0 text-muted-foreground opacity-50"
              />
            )}
          </li>
        ))}
      </ol>
      {fanout !== null && <FanoutLane step={fanout} />}
    </div>
  );
}

function Band({
  band,
  onSelectStep,
  selectedStep,
  designatedStep,
}: {
  band: PhaseLaneBand;
  onSelectStep?: (name: string) => void;
  selectedStep: string | null;
  /**
   * Whose children the fan-out lane is actually drawing — `designatedFanout`'s
   * answer, not the raw selection. The two differ on every view where nothing
   * is selected yet, and the width mark is pressed-state about the ENUMERATION,
   * so it has to be keyed to this rather than to `selectedStep` (Rupert PR-1400
   * r0).
   */
  designatedStep: string | null;
}): JSX.Element {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1.5 rounded-lg border border-border-subtle bg-panel-well p-2.5">
      <p className="label-micro text-muted-foreground">
        {band.over.length === 0 ? (
          "runs once"
        ) : (
          <>
            for each{" "}
            {band.over.map((source, i) => (
              <React.Fragment key={source}>
                {i > 0 && (
                  <>
                    <span aria-hidden> × </span>
                    {/* "×" reads as nothing useful aloud, and the nesting is
                        the point: this band runs once per (fragment, torsion)
                        pair, not once per fragment. */}
                    <span className="sr-only"> and each </span>
                  </>
                )}
                <span className="font-mono normal-case">{source}</span>
              </React.Fragment>
            ))}
          </>
        )}
      </p>
      <ul className="flex flex-col gap-1">
        {band.steps.map((step) => (
          <li key={step.name} className="flex flex-col gap-1">
            <div className="flex items-center gap-1.5">
              {step.status !== null && (
                <span className="flex shrink-0 items-center">
                  <span
                    className={cn("h-1.5 w-1.5 rounded-full", STATE_STRIPE[step.status])}
                    aria-hidden
                  />
                  {/* Colour is never the only channel — `RunStateBadge` says the
                      state in words wherever there is room, and where there is
                      not (a 6px dot) it is said to a screen reader instead. */}
                  <span className="sr-only">{STATE_LABEL[step.status]}: </span>
                </span>
              )}
              <StepName
                name={step.name}
                onSelect={onSelectStep}
                selected={step.name === selectedStep}
              />
              {step.width !== null && (
                <FanoutWidth
                  name={step.name}
                  width={step.width}
                  onSelect={onSelectStep}
                  selected={step.name === designatedStep}
                />
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The `×N` width mark — and, where the lane has a selection to move, **the
 * control that opens that step's children** (flowstack#1398).
 *
 * `designatedFanout` has always been able to point the enumeration at any
 * fanned step; until now the only thing that moved it was the step's NAME,
 * which is the wrong target for the intent. `×6` is the affordance a reader
 * reaches for when they want to see the six — the operator pressed it, and the
 * mark was a `<span>`, so nothing happened. That is the defect: not a missing
 * capability, an unreachable one.
 *
 * **It presses like the step name presses**, and it does the same thing: it
 * SELECTS the step. So the rail opens on that step's settings at the same time
 * as its children are enumerated — one selection, two consequences, rather than
 * a second piece of lane state that could disagree with the rail. And because
 * selection is single, pressing one width mark moves the enumeration off
 * whichever step held it: the lane never draws two fan-outs at once, which is
 * flowstack#1377 and the reason this lane exists in its current form.
 *
 * **Its accessible name says what pressing does, not what it reads.** `×6`
 * spoken is nothing; ", 6 runs" was the honest description of a static mark but
 * describes no action. As a button it announces the act and its object, and
 * `aria-pressed` carries whether these are the children currently shown.
 *
 * **`selected` is the DESIGNATED fan-out, never the raw `selectedStep`** (Rupert
 * PR-1400 r0). `designatedFanout` falls back to the frontier when nothing is
 * selected, so on first view — the state every reader meets first — keying the
 * pressed state to the selection announces "Show …", `aria-pressed=false`, on
 * the one button whose children are already on screen. That is a screen reader
 * being told the open thing is closed, inside the affordance added to fix an
 * accessibility defect. Both channels read the value that decides what renders.
 *
 * With no `onSelect` — the submit page's template lane, which has no run and so
 * no widths at all — it stays exactly the static mark it was. A control that
 * did nothing would be the defect again, wearing the fix's clothes.
 */
function FanoutWidth({
  name,
  width,
  selected,
  onSelect,
}: {
  name: string;
  width: number;
  selected: boolean;
  onSelect?: (name: string) => void;
}): JSX.Element {
  if (onSelect === undefined)
    return (
      <span className="shrink-0 font-mono text-[10px] font-semibold text-cyan">
        <span aria-hidden>×{width}</span>
        <span className="sr-only">, {width} runs</span>
      </span>
    );
  return (
    <button
      type="button"
      data-phase-lane-fanout={name}
      onClick={() => onSelect(name)}
      aria-pressed={selected}
      className={cn(
        // A hairline box is the whole affordance budget here: the mark sits at
        // 10px beside a step name and anything heavier would out-shout the
        // lifecycle fill, which is the one thing on this row that must be read
        // first. Hue stays where it was — it is data (the width), not chrome.
        "shrink-0 rounded-sm border px-1 font-mono text-[10px] font-semibold text-cyan",
        "ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        selected
          ? "border-cyan/60 bg-cyan/10"
          : "border-cyan/25 hover:border-cyan/60 hover:bg-cyan/10"
      )}
    >
      <span aria-hidden>×{width}</span>
      <span className="sr-only">
        {selected ? "Showing" : "Show"} the {width} fan-out children of {name}
      </span>
    </button>
  );
}

/**
 * How many child rows a fanned step draws before it stops (flowstack#1281).
 *
 * The lane's whole claim is that it stays readable in a 400 px pane at the
 * operator's 1280×679, and it WRAPS rather than scrolling — so an unbounded
 * enumeration is the failure mode this surface was built to avoid, not a
 * feature of it. The AFFDO template fans 1800 ways at `dft_constrained_opt`,
 * so the cap is load-bearing even now that only ONE step is enumerated
 * (flowstack#1377) — that step alone can be the whole page.
 *
 * Six, because that is the width the canvas draws and the widest the golden
 * runs actually produce (they fan 2, 3, 5 and 6 ways), so on real work the
 * enumeration is COMPLETE and the truncation never fires. Past it the lane
 * says how many it withheld and sends the reader to the step, where
 * `NodeDetailSheet`'s Children section already lists every child with its
 * state. Truncating rather than scrolling keeps the band a fixed height, which
 * is what lets bands wrap as text.
 */
const CHILD_ROW_LIMIT = 6;

/**
 * The fan-out lane: ONE designated step's children, one row per child, each
 * with its own id, state and duration — beside the span rather than inside it
 * (flowstack#1377).
 *
 * It is headed the way the canvas heads its lane — `torsion_fit · fan-out ×6`
 * — because a list of run ids detached from the span has to say whose it is;
 * inline under the step that was free, and it is what moving the rows out
 * costs.
 *
 * **The accessibility contract is per ROW, not per step.** A step's own dot is
 * followed by its state said out loud; a list of coloured dots carrying the
 * thing the reader came for would be a regression dressed as a feature. So
 * every row repeats the contract — a `sr-only` state before the id, and a
 * duration whose unit is spoken rather than left as a bare glyph.
 *
 * A `bg-panel-well` block, the same ground a band stands on, so the lane reads
 * as a sibling of the bands rather than as a second panel vocabulary.
 */
function FanoutLane({ step }: { step: PhaseLaneStep }): JSX.Element {
  const shown = step.children.slice(0, CHILD_ROW_LIMIT);
  // Against `width`, not against `children.length` — the server caps the rows
  // it sends and never the count, so the honest remainder is measured from the
  // step's true width. `width` is null on the one shape that has rows without
  // a width — a node whose expansion faulted after a single dispatch — and
  // there `?? shown.length` yields a remainder of zero, which is the truth:
  // that row is the whole list, not a prefix of a longer one.
  const total = step.width ?? shown.length;
  const withheld = total - shown.length;
  return (
    <div className="flex shrink-0 flex-col gap-1.5 rounded-lg border border-border-subtle bg-panel-well p-2.5 pair:w-[15rem]">
      <p className="label-micro text-muted-foreground">
        <span className="font-mono normal-case">{step.name}</span> · fan-out{" "}
        {/* The multiplicity in digits, exactly as the step's own badge carries
            it — the heading is what tells the reader these rows are a PREFIX
            of `total`, not the whole of it. */}
        <span aria-hidden>×{total}</span>
        <span className="sr-only">, {total} runs</span>
      </p>
      <ul aria-label={`${step.name} fan-out children`} className="flex flex-col gap-0.5 pl-3">
        {shown.map((child) => (
          <ChildRow key={child.runId} child={child} />
        ))}
        {withheld > 0 && (
          <li className="text-[10px] text-muted-foreground">
            + {withheld.toLocaleString()} more — open {step.name} for the full list
          </li>
        )}
      </ul>
    </div>
  );
}

function ChildRow({ child }: { child: PhaseLaneChild }): JSX.Element {
  return (
    <li className="flex items-center gap-1.5">
      <span className="flex shrink-0 items-center">
        <span className={cn("h-1.5 w-1.5 rounded-full", STATE_STRIPE[child.status])} aria-hidden />
        {/* Same contract as the step above it: the fill is never the only
            channel that carries the state. */}
        <span className="sr-only">{STATE_LABEL[child.status]}: </span>
      </span>
      {/* The run id elided in the middle, with the full value on `title` — a
          child run has no other name on the wire, and `truncateMiddle` is the
          app's one way to shorten a machine identifier. */}
      <span title={child.runId} className="min-w-0 flex-1 truncate font-mono text-[10px]">
        {truncateMiddle(child.runId, 22)}
      </span>
      <span className="shrink-0 font-mono text-[10px] text-muted-foreground">
        <span aria-hidden>{formatDuration(child.durationSeconds)}</span>
        {/* "—" and "38.1s" both read as noise aloud; say what the number is. */}
        <span className="sr-only">
          {child.durationSeconds === null
            ? ", no duration recorded"
            : `, ran ${formatDuration(child.durationSeconds)}`}
        </span>
      </span>
    </li>
  );
}

function StepName({
  name,
  selected,
  onSelect,
}: {
  name: string;
  selected: boolean;
  onSelect?: (name: string) => void;
}): JSX.Element {
  const className = "min-w-0 flex-1 truncate text-left font-mono text-[11px] font-medium";
  // The marker is what lets a test say "these are the flow's STEPS" — the band
  // headings above them are mono node names too, so a text query alone cannot
  // tell a step from the scope it repeats over.
  if (onSelect === undefined)
    return (
      <span data-phase-lane-step={name} className={className}>
        {name}
      </span>
    );
  return (
    <button
      type="button"
      data-phase-lane-step={name}
      onClick={() => onSelect(name)}
      aria-pressed={selected}
      className={cn(
        className,
        "rounded-sm ring-offset-background transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        selected ? "text-foreground underline decoration-dotted underline-offset-4" : null
      )}
    >
      {name}
    </button>
  );
}

/** Which reading of a flow's structure a pane is showing. */
export type FlowShapeView = "lane" | "graph";

/**
 * The switch between the two readings.
 *
 * A segmented control on the same `bg-panel-well` track as the run workspace's
 * mode switch, and with the same semantics for the same reason: a
 * `role="group"` of `aria-pressed` buttons rather than a radiogroup, because
 * each button ACTS on press — there is no committed selection separate from the
 * act — and radio semantics would mean writing roving focus for a two-item
 * group `Tab` already reaches correctly.
 *
 * It exists so the graph is never further than one press away. VIS/V2's whole
 * scope is a reading aid for the predetermined case, and a reading aid that
 * takes the graph away has replaced it instead.
 */
export function FlowShapeSwitch({
  view,
  onSelect,
  className,
}: {
  view: FlowShapeView;
  onSelect: (view: FlowShapeView) => void;
  className?: string;
}): JSX.Element {
  return (
    <div
      role="group"
      aria-label="Flow structure"
      className={cn(
        "flex items-center gap-0.5 rounded-lg border border-border-subtle bg-panel-well p-[3px]",
        className
      )}
    >
      <ShapeButton
        label="Pipeline"
        icon={Rows3}
        active={view === "lane"}
        onSelect={() => onSelect("lane")}
      />
      <ShapeButton
        label="Graph"
        icon={Network}
        active={view === "graph"}
        onSelect={() => onSelect("graph")}
      />
    </div>
  );
}

function ShapeButton({
  label,
  icon: Icon,
  active,
  onSelect,
}: {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  active: boolean;
  onSelect: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium transition-colors",
        active ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
      )}
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden />
      {label}
    </button>
  );
}
